import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { imgUrl } from "@/lib/imageUrl";

export default function ProductDialog({ product, addons, onClose }) {
  // selectedByGroup[addonId] = Set of choiceIds (for groups) | true (for simple addon)
  const [selectedByGroup, setSelectedByGroup] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [comment, setComment] = useState("");
  const { addItem } = useCart();

  const isGroup = (a) => Array.isArray(a.choices) && a.choices.length > 0;

  const toggleSimple = (a) => {
    setSelectedByGroup((prev) => {
      const next = { ...prev };
      if (next[a.id]) delete next[a.id];
      else next[a.id] = true;
      return next;
    });
  };

  const toggleChoice = (a, c) => {
    setSelectedByGroup((prev) => {
      const next = { ...prev };
      const current = new Set(next[a.id] instanceof Set ? next[a.id] : []);
      if (a.multi === false) {
        // single choice: replace
        if (current.has(c.id)) {
          current.delete(c.id);
        } else {
          current.clear();
          current.add(c.id);
        }
      } else {
        if (current.has(c.id)) current.delete(c.id);
        else current.add(c.id);
      }
      if (current.size === 0) delete next[a.id];
      else next[a.id] = current;
      return next;
    });
  };

  // Flatten selection into the addons list sent to the cart
  const flatSelected = [];
  for (const a of addons) {
    if (isGroup(a)) {
      const set = selectedByGroup[a.id];
      if (set instanceof Set) {
        for (const cid of set) {
          const c = a.choices.find((x) => x.id === cid);
          if (c) flatSelected.push({ id: `${a.id}__${c.id}`, name: `${a.name}: ${c.name}`, price: Number(c.price || 0) });
        }
      }
    } else {
      if (selectedByGroup[a.id] === true) {
        flatSelected.push({ id: a.id, name: a.name, price: Number(a.price || 0) });
      }
    }
  }

  const addonsTotal = flatSelected.reduce((s, a) => s + a.price, 0);
  const total = (product.price + addonsTotal) * quantity;

  const handleAdd = () => {
    // Validate required groups
    for (const a of addons) {
      if (isGroup(a) && a.required) {
        const set = selectedByGroup[a.id];
        if (!(set instanceof Set) || set.size === 0) {
          toast.error(`Veuillez choisir : ${a.name}`);
          return;
        }
      }
    }
    addItem({
      product_id: product.id, name: product.name, price: product.price,
      image: product.image, addons: flatSelected, quantity, comment,
    });
    toast.success(`${quantity}× ${product.name} ajouté au panier`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="product-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="p-0 gap-0 overflow-hidden
          w-screen h-[100dvh] max-w-full max-h-[100dvh] rounded-none border-0 top-0 left-0 translate-x-0 translate-y-0
          sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-full sm:max-w-lg sm:h-auto sm:max-h-[90vh] sm:rounded-sm sm:border
          flex flex-col"
      >
        {product.image && (
          <div className="h-48 sm:h-56 overflow-hidden bg-secondary shrink-0">
            <img src={imgUrl(product.image)} alt={product.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 pb-24 sm:pb-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="font-display text-2xl sm:text-3xl text-left">{product.name}</DialogTitle>
            <DialogDescription className="text-left text-sm sm:text-base">{product.description}</DialogDescription>
          </DialogHeader>

          {addons.map((a) => {
            if (isGroup(a)) {
              const set = selectedByGroup[a.id] instanceof Set ? selectedByGroup[a.id] : new Set();
              const single = a.multi === false;
              return (
                <div key={a.id} className="mb-5" data-testid={`addon-group-${a.id}`}>
                  <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
                    <h4 className="uppercase tracking-[0.2em] text-xs text-muted-foreground">{a.name}</h4>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {a.required ? "Obligatoire" : "Optionnel"} · {single ? "1 choix" : "Plusieurs"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {a.choices.map((c) => {
                      const checked = set.has(c.id);
                      return (
                        <label
                          key={c.id}
                          data-testid={`choice-${c.id}`}
                          className={`flex items-center justify-between p-3 border rounded-sm cursor-pointer transition ${checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
                        >
                          <span className="flex items-center gap-3">
                            {single ? (
                              <input
                                type="radio"
                                name={`group-${a.id}`}
                                checked={checked}
                                onChange={() => toggleChoice(a, c)}
                                className="accent-primary"
                              />
                            ) : (
                              <Checkbox checked={checked} onCheckedChange={() => toggleChoice(a, c)} />
                            )}
                            <span className="text-sm">{c.name}</span>
                          </span>
                          <span className="text-sm text-primary font-medium shrink-0">
                            {Number(c.price) > 0 ? `+${Number(c.price).toFixed(2)} CHF` : "Inclus"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            }
            // simple addon
            const checked = selectedByGroup[a.id] === true;
            return (
              <label
                key={a.id}
                data-testid={`addon-${a.id}`}
                className={`mb-2 flex items-center justify-between p-3 border rounded-sm cursor-pointer transition ${checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
              >
                <span className="flex items-center gap-3">
                  <Checkbox checked={checked} onCheckedChange={() => toggleSimple(a)} />
                  <span className="text-sm">{a.name}</span>
                </span>
                <span className="text-sm text-primary font-medium">+{Number(a.price || 0).toFixed(2)} CHF</span>
              </label>
            );
          })}

          <div className="mb-4 mt-4">
            <h4 className="uppercase tracking-[0.2em] text-xs text-muted-foreground mb-3">Commentaire (optionnel)</h4>
            <Textarea
              data-testid="product-comment"
              placeholder="ex : sans oignons, peu épicé…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={200}
              autoFocus={false}
              className="rounded-sm"
            />
          </div>
        </div>

        {/* Sticky footer (always visible on mobile & desktop) */}
        <div className="border-t border-border bg-white p-4 sm:p-5 shrink-0 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center border border-border rounded-sm shrink-0">
            <button data-testid="qty-minus" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-secondary" aria-label="Diminuer"><Minus size={16} /></button>
            <span data-testid="qty-value" className="px-3 font-medium min-w-[32px] text-center">{quantity}</span>
            <button data-testid="qty-plus" onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-secondary" aria-label="Augmenter"><Plus size={16} /></button>
          </div>
          <button
            data-testid="add-to-cart-btn"
            onClick={handleAdd}
            className="flex-1 bg-primary text-white py-3 rounded-sm hover:bg-primary/90 transition font-medium text-sm sm:text-base"
          >
            Ajouter · {total.toFixed(2)} CHF
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
